import React from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ContainerSummary} from 'constants/SampleConstants';
import {
  checkSamplesForStatusWarnings,
  SampleStatusWarning,
} from 'inventory/sample-status/SampleStatusWarning';
import {BaseSampleStatusWarningTrackingParams} from 'inventory/sample-status/SampleStatusWarningTracking';
import {
  ChooseTransferOutputPaneState,
  TransferPaneSampleStatusState,
  UpdateTransferModalStateHandler,
  UpdateTransferPaneSampleStatusStateHandler,
} from 'samples/transfers/TransferModalUtil';
import {InlineBannerType} from 'ui-library/banners/InlineBanner';
import InlineBannerStack from 'ui-library/banners/InlineBannerStack';
import {Column, Table} from 'ui-library/simple-table/SimpleTable';

import StorableSearchInput from '../storables/StorableSearchInput';

interface ContainerTableProps {
  containers: Array<ContainerSummary>;
}

function ContainerTable({containers}: ContainerTableProps): JSX.Element {
  return (
    <Table className="u-small-spacing-top" numRows={containers.length}>
      <Column header="Barcode" renderCell={(rowIndex) => containers[rowIndex].barcode} />
      <Column header="Name" renderCell={(rowIndex) => containers[rowIndex].name} />
      <Column header="Schema" renderCell={(rowIndex) => containers[rowIndex].storableSchema.name} />
    </Table>
  );
}

export interface ChooseOutputContainersProps {
  containers: Array<ContainerSummary>;
  errors: Array<string>;
  registryId?: string;
  sampleStatusWarningTrackingParams: BaseSampleStatusWarningTrackingParams;
  outputSampleStatusState: TransferPaneSampleStatusState;
  onUpdateSampleStatusState: UpdateTransferPaneSampleStatusStateHandler;
  onUpdateModalState: UpdateTransferModalStateHandler;
}

class ChooseOutputContainers extends React.Component<ChooseOutputContainersProps> {
  componentDidMount(): void {
    if (
      this.props.containers.length > 0 &&
      this.props.outputSampleStatusState.warningInputOutputType === ModelTypeConstants.PLATE
    ) {
      const updatedPaneStateWithWarnings = this.updateOutputSampleStatusWarnings(this.props.containers);
      this.props.onUpdateModalState(updatedPaneStateWithWarnings);
    }
  }

  private handleContainersChange = (containers: Array<ContainerSummary>): void => {
    const updatedPaneStateWithWarnings = this.updateOutputSampleStatusWarnings(containers);
    this.props.onUpdateModalState({
      ...updatedPaneStateWithWarnings,
      outputContainerSummaries: containers,
    });
  };

  private updateOutputSampleStatusWarnings = (
    containers: Array<ContainerSummary>
  ): Partial<ChooseTransferOutputPaneState> => {
    const {shouldShowWarning, samples} = checkSamplesForStatusWarnings({samples: containers});
    const selectedSampleIds = samples?.map(({id}) => id) ?? [];
    const newOutputStatusState = {
      ...this.props.outputSampleStatusState,
      shouldShowStatusWarnings: shouldShowWarning,
      canProceedOnSampleStatus: false,
      selectedSampleIds,
      samples: samples ?? null,
      warningInputOutputType: ModelTypeConstants.CONTAINER as const,
    };
    return {
      outputSampleStatusState: newOutputStatusState,
      confirmedOutputContainerIds: shouldShowWarning ? selectedSampleIds : null,
    };
  };

  private setCanProceedOnSampleStatus = (override: boolean): void => {
    this.props.onUpdateSampleStatusState({canProceedOnSampleStatus: override}, 'outputSampleStatusState');
  };

  private setSelectedSampleIds = (sampleIds: Array<string>): void => {
    this.props.onUpdateSampleStatusState({selectedSampleIds: sampleIds}, 'outputSampleStatusState');
    this.props.onUpdateModalState({confirmedOutputContainerIds: sampleIds});
  };

  private renderErrorBanners(): JSX.Element {
    return (
      <div className="u-small-spacing-top">
        <InlineBannerStack
          messages={this.props.errors.map((message) => ({
            bannerType: InlineBannerType.ERROR,
            text: message,
          }))}
        />
      </div>
    );
  }

  renderWarningOrContainerTable(): JSX.Element {
    return this.props.outputSampleStatusState.shouldShowStatusWarnings &&
      this.props.outputSampleStatusState.warningInputOutputType === ModelTypeConstants.CONTAINER &&
      this.props.outputSampleStatusState.samples != null ? (
      <>
        {this.renderErrorBanners()}
        <div className="u-small-spacing-top">
          <SampleStatusWarning
            sampleIds={this.props.containers.map(({id}) => id)}
            selectedSampleIds={this.props.outputSampleStatusState.selectedSampleIds}
            setSelectedSampleIds={this.setSelectedSampleIds}
            shouldProceed={this.props.outputSampleStatusState.canProceedOnSampleStatus}
            setShouldProceed={this.setCanProceedOnSampleStatus}
            trackingParams={this.props.sampleStatusWarningTrackingParams}
            samples={this.props.outputSampleStatusState.samples}
          />
        </div>
      </>
    ) : (
      <>
        {this.renderErrorBanners()}
        <ContainerTable containers={this.props.containers} />
      </>
    );
  }

  render(): JSX.Element {
    return (
      <div>
        <StorableSearchInput
          onChange={this.handleContainersChange}
          registryId={this.props.registryId}
          selectedStorables={this.props.containers}
          shouldAllowMultiple={true}
          hideSelectedResults={true}
          storableTypes={[ModelTypeConstants.CONTAINER]}
        />
        {this.props.containers.length > 0 && <>{this.renderWarningOrContainerTable()}</>}
      </div>
    );
  }
}

export default ChooseOutputContainers;
